import React, { Fragment } from "react";

import img13 from "../../../images/service/E-Commerce Blockchain Development/automobile.png";
import img14 from "../../../images/service/E-Commerce Blockchain Development/automobile (2).png";
import img15 from "../../../images/service/E-Commerce Blockchain Development/automobile (3).png";

// Service icons
import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

// Common Components
import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";
import Industries from "../../../components/Industries/Industries";
import TheorySection from "../../../components/PageSection/Theory";

// =====================
// DATA
// =====================

const serviceCards = [
  {
    icon: sIcon1,
    title: "Vehicle Identity & History",
    content:
      "Create immutable digital identities for vehicles with complete ownership history, maintenance records, and accident reports.",
  },
  {
    icon: sIcon2,
    title: "Smart Manufacturing Tracking",
    content:
      "Track components and assemblies throughout the manufacturing process ensuring quality and preventing counterfeit parts.",
  },
  {
    icon: sIcon3,
    title: "Connected Vehicle Ecosystems",
    content:
      "Enable secure vehicle-to-vehicle communication, autonomous driving data sharing, and usage-based insurance models.",
  },
];

const benefits = [
  {
    title: "Tamper-Proof Records",
    description:
      "Immutable blockchain records prevent odometer fraud, title washing, and manipulation of vehicle history data.",
    icon: sIcon1,
  },
  {
    title: "Supply Chain Transparency",
    description:
      "Track automotive parts from manufacturing to installation, ensuring authenticity and preventing counterfeit components.",
    icon: sIcon2,
  },
  {
    title: "Streamlined Transactions",
    description:
      "Automate vehicle sales, leasing, and registration processes with smart contracts reducing paperwork and processing time.",
    icon: sIcon3,
  },
  {
    title: "Enhanced Data Security",
    description:
      "Protect sensitive vehicle data, owner information, and connected car telemetry with cryptographic security.",
    icon: sIcon4,
  },
];

const faqs = [
  {
    q: "What is automobile blockchain development?",
    a: "Automobile blockchain development creates secure, transparent systems for vehicle identity, supply chain tracking, connected car data management, and automotive transactions using distributed ledger technology.",
  },
  {
    q: "How does blockchain benefit the automotive industry?",
    a: "Blockchain provides tamper-proof vehicle histories, prevents counterfeit parts, streamlines transactions, enables usage-based insurance, and creates secure ecosystems for connected and autonomous vehicles.",
  },
  {
    q: "What data can be stored on automotive blockchain?",
    a: "Vehicle identification, ownership history, maintenance records, accident reports, mileage, parts provenance, warranty information, insurance claims, and connected vehicle telemetry data.",
  },
  {
    q: "Is blockchain secure for vehicle data?",
    a: "Yes—blockchain's cryptographic security and decentralized structure protect vehicle data from tampering while enabling authorized access for manufacturers, dealers, insurers, and owners.",
  },
];

// =====================
// PAGE COMPONENT
// =====================

export default function AutomobileBlockchainDevelopment() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="Automobile Blockchain Development"
        pagesub="Drive innovation with secure, transparent automotive blockchain solutions"
      />

      {/* HERO SECTION */}
      <HeroSection
        title={
          <>
            Transform the{" "}
            <span className="gradient-text">Automotive Industry</span>{" "}
            with Blockchain
          </>
        }
        subtitle={`Leverage blockchain technology to create transparent vehicle ecosystems with tamper-proof histories, 
secure supply chains, and innovative solutions for connected and autonomous vehicles.`}
        buttonText="Book Consultation"
        buttonLink="/contact"
        image={img13}
        imageAlt="Automobile Blockchain Development"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Why Blockchain for Automotive?"
        image={img14}
        paragraphs={[
          "Blockchain revolutionizes the automotive industry by providing immutable vehicle identities, transparent supply chains, and secure data sharing for connected vehicles. Every stakeholder benefits from trusted, verifiable information.",
          "From preventing odometer fraud to enabling autonomous vehicle data marketplaces, blockchain creates new business models while solving longstanding challenges in vehicle history verification and supply chain transparency.",
        ]}
      />

      {/* SERVICES */}
      <ServiceCardsSection
        title="Core Automobile Blockchain Services"
        cards={serviceCards}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Challenges in Traditional Automotive Systems"
        image={img15}
        paragraphs={[
          "Traditional automotive systems struggle with vehicle history fraud, counterfeit parts infiltration, inefficient transaction processes, data silos between stakeholders, and security concerns for connected vehicles.",
          "Blockchain addresses these issues by creating single sources of truth for vehicle data, automating processes with smart contracts, and providing secure, decentralized infrastructure for automotive ecosystems.",
        ]}
      />

      {/* BENEFITS */}
      <Benefit
        title="Benefits of Blockchain in Automotive"
        benefits={benefits}
      />

      {/* KEY FEATURES */}
      <KeyFeaturesSection
        title="Key Features of Our Automobile Blockchain Solutions"
        subtitle="Build trust and innovation in automotive ecosystems with cutting-edge technology"
        features={[
          {
            icon: sIcon1,
            title: "Digital Vehicle Twin",
            description:
              "Create comprehensive blockchain-based digital identities for every vehicle with complete lifecycle data.",
          },
          {
            icon: sIcon2,
            title: "Parts Provenance Tracking",
            description:
              "Verify authenticity of components from manufacturing through installation and replacement.",
          },
          {
            icon: sIcon3,
            title: "Automated Warranties",
            description:
              "Smart contracts manage warranty claims and service scheduling based on vehicle usage data.",
          },
          {
            icon: sIcon4,
            title: "Usage-Based Insurance",
            description:
              "Enable fair insurance premiums based on verified driving behavior and vehicle usage patterns.",
          },
          {
            icon: sIcon5,
            title: "V2V Communication",
            description:
              "Secure vehicle-to-vehicle data sharing for traffic optimization and autonomous driving.",
          },
          {
            icon: sIcon6,
            title: "Maintenance Verification",
            description:
              "Immutable service records increase resale value and provide transparency for buyers.",
          },
        ]}
      />

      <Industries />

      {/* WHY CHOOSE US */}
      <WhyChooseUsSection
        items={[
          "Deep expertise in automotive technology and blockchain integration",
          "Proven solutions for manufacturers, dealers, and service providers",
          "Focus on security, scalability, and industry standard compliance",
        ]}
      />

      {/* FAQ */}
      <FaqSection faqs={faqs} />

      {/* FINAL CTA */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
